import React, { useEffect, useState } from "react";
import { Bell, BellSlash, CheckCircle, DeviceMobile } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  pushSupported, getPushPermission, subscribeToPush, unsubscribeFromPush,
  sendTestPush, isCurrentlySubscribed,
} from "../lib/push";

export default function PushPrompt() {
  const [supported, setSupported] = useState(true);
  const [permission, setPermission] = useState("default");
  const [subbed, setSubbed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(
    typeof window !== "undefined" && localStorage.getItem("mhh.push.dismiss") === "1"
  );

  const refresh = async () => {
    setSupported(pushSupported());
    if (!pushSupported()) return;
    setPermission(await getPushPermission());
    setSubbed(await isCurrentlySubscribed());
  };

  useEffect(() => { refresh(); }, []);

  const enable = async () => {
    setBusy(true);
    try {
      await subscribeToPush();
      toast.success("Push notifications enabled");
      await refresh();
    } catch (e) {
      toast.error(e.message || "Could not enable push");
    } finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true);
    try {
      await unsubscribeFromPush();
      toast.success("Push notifications disabled");
      await refresh();
    } finally { setBusy(false); }
  };
  const test = async () => {
    try { await sendTestPush(); toast.success("Test sent — check your device"); }
    catch { toast.error("Test failed"); }
  };

  if (dismissed) return null;

  if (!supported) {
    return (
      <div className="brand-card p-4 flex items-center gap-3" data-testid="push-unsupported">
        <BellSlash size={20} weight="duotone" className="text-[var(--text-muted)]" />
        <p className="text-sm text-[var(--text-muted)] flex-1">Your browser doesn't support push notifications. We'll keep emailing you instead.</p>
      </div>
    );
  }

  if (subbed && permission === "granted") {
    return (
      <div className="brand-card p-4 flex items-center gap-3" data-testid="push-active">
        <CheckCircle size={20} weight="fill" className="text-[var(--brand-orange)]" />
        <p className="text-sm flex-1"><strong className="text-[var(--brand-green)]">Push notifications on.</strong> You'll get instant lock-screen alerts for matching jobs.</p>
        <button onClick={test} className="btn-ghost text-xs" data-testid="push-test-btn">Test</button>
        <button onClick={disable} disabled={busy} className="btn-ghost text-xs" data-testid="push-disable-btn">Turn off</button>
      </div>
    );
  }

  return (
    <div className="brand-card p-5 noise-bg" style={{ background: "var(--brand-green)", color: "#fff" }} data-testid="push-prompt">
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <Bell size={32} weight="duotone" className="text-[var(--brand-orange)] flex-shrink-0" />
        <div className="flex-1">
          <p className="font-display font-bold text-lg">Turn on instant job alerts</p>
          <p className="text-white/85 text-sm">Get a lock-screen notification the second a matching job is posted. No more refreshing your inbox.</p>
          <p className="text-white/70 text-xs mt-2 flex items-center gap-1"><DeviceMobile size={12} /> Tip: add MyHandyHub to your home screen first for the best experience.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={enable} disabled={busy} className="btn-accent" data-testid="push-enable-btn">{busy ? "Enabling…" : "Enable"}</button>
          <button onClick={() => { localStorage.setItem("mhh.push.dismiss", "1"); setDismissed(true); }} className="px-3 py-2 text-white/80 text-sm hover:text-white" data-testid="push-dismiss-btn">Later</button>
        </div>
      </div>
    </div>
  );
}
